import {
    ProductAnalysisEntity,
    ProductAnalysisProblemEntity,
    ProductAnalysisReviewTimelineEntity,
} from '@/entities/product-analysis.entity';

export const PRODUCT_ANALYSIS_MS_IN_DAY = 24 * 60 * 60 * 1000;

export type ProductAnalysisTimeWindow = {
    startTimestamp: number;
    endTimestamp: number;
};

export type ProductAnalysisTimelineReview = ProductAnalysisReviewTimelineEntity & {
    timestamp: number;
};

export type ProductAnalysisTimelineBounds = {
    minTimestamp: number;
    maxTimestamp: number;
    totalDays: number;
};

export type ProductAnalysisTimelineControls = {
    totalWindowDays: number;
    normalizedWindowWidthDays: number;
    maxWindowStartOffsetDays: number;
    normalizedWindowStartOffsetDays: number;
    maxTimelineAggregationWindowDays: number;
    normalizedTimelineAggregationWindowDays: number;
};

export type ProductAnalysisReviewsTimelinePoint = {
    startTimestamp: number;
    endTimestamp: number;
    reviewsCount: number;
};

const toFiniteInteger = (value: number, fallback: number): number => {
    if (!Number.isFinite(value)) {
        return fallback;
    }

    return Math.round(value);
};

export const clampProductAnalysisValue = (value: number, min: number, max: number): number => {
    return Math.min(Math.max(value, min), max);
};

export const parseProductAnalysisTimestamp = (value: string): number | null => {
    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? null : timestamp;
};

export const buildProductAnalysisTimeline = (data: ProductAnalysisEntity | null): ProductAnalysisTimelineReview[] => {
    if (!data) {
        return [];
    }

    if (Array.isArray(data.reviewTimeline) && data.reviewTimeline.length) {
        return data.reviewTimeline
            .flatMap((row) => {
                const timestamp = parseProductAnalysisTimestamp(row.createdDate);
                if (timestamp === null) {
                    return [];
                }

                return [{ ...row, timestamp }];
            })
            .sort((left, right) => left.timestamp - right.timestamp);
    }

    const fallbackMap = new Map<string, ProductAnalysisTimelineReview>();
    for (const row of [...data.negativeProblems, ...data.positiveProblems, ...(data.neutralProblems || [])]) {
        if (fallbackMap.has(row.reviewId)) {
            continue;
        }

        const timestamp = parseProductAnalysisTimestamp(row.createdDate);
        if (timestamp === null) {
            continue;
        }

        fallbackMap.set(row.reviewId, {
            reviewId: row.reviewId,
            createdDate: row.createdDate,
            hasAnalysis: true,
            timestamp,
        });
    }

    return [...fallbackMap.values()].sort((left, right) => left.timestamp - right.timestamp);
};

export const buildProductAnalysisReviewTimestampMap = (
    reviewTimeline: ProductAnalysisTimelineReview[],
): Map<string, number> => {
    const timestamps = new Map<string, number>();
    for (const review of reviewTimeline) {
        timestamps.set(review.reviewId, review.timestamp);
    }

    return timestamps;
};

export const buildProductAnalysisTimelineBounds = (
    reviewTimeline: ProductAnalysisTimelineReview[],
): ProductAnalysisTimelineBounds | null => {
    if (!reviewTimeline.length) {
        return null;
    }

    const minTimestamp = reviewTimeline[0].timestamp;
    const maxTimestamp = reviewTimeline[reviewTimeline.length - 1].timestamp;
    const totalDays = Math.max(1, Math.ceil((maxTimestamp - minTimestamp) / PRODUCT_ANALYSIS_MS_IN_DAY) + 1);

    return {
        minTimestamp,
        maxTimestamp,
        totalDays,
    };
};

export const normalizeProductAnalysisTimelineControls = ({
    timelineBounds,
    windowWidthDays,
    windowStartOffsetDays,
    timelineAggregationWindowDays,
}: {
    timelineBounds: ProductAnalysisTimelineBounds | null;
    windowWidthDays: number;
    windowStartOffsetDays: number;
    timelineAggregationWindowDays: number;
}): ProductAnalysisTimelineControls => {
    const totalWindowDays = Math.max(1, timelineBounds?.totalDays ?? 1);
    const normalizedWindowWidthDays = clampProductAnalysisValue(
        toFiniteInteger(windowWidthDays, 1),
        1,
        totalWindowDays,
    );
    const maxWindowStartOffsetDays = Math.max(0, totalWindowDays - normalizedWindowWidthDays);
    const normalizedWindowStartOffsetDays = clampProductAnalysisValue(
        toFiniteInteger(windowStartOffsetDays, 0),
        0,
        maxWindowStartOffsetDays,
    );
    const maxTimelineAggregationWindowDays = Math.max(1, normalizedWindowWidthDays);
    const normalizedTimelineAggregationWindowDays = clampProductAnalysisValue(
        toFiniteInteger(timelineAggregationWindowDays, 1),
        1,
        maxTimelineAggregationWindowDays,
    );

    return {
        totalWindowDays,
        normalizedWindowWidthDays,
        maxWindowStartOffsetDays,
        normalizedWindowStartOffsetDays,
        maxTimelineAggregationWindowDays,
        normalizedTimelineAggregationWindowDays,
    };
};

export const buildProductAnalysisTimeWindow = ({
    timelineBounds,
    normalizedWindowWidthDays,
    normalizedWindowStartOffsetDays,
}: {
    timelineBounds: ProductAnalysisTimelineBounds | null;
    normalizedWindowWidthDays: number;
    normalizedWindowStartOffsetDays: number;
}): ProductAnalysisTimeWindow | null => {
    if (!timelineBounds) {
        return null;
    }

    const startTimestamp = timelineBounds.minTimestamp + normalizedWindowStartOffsetDays * PRODUCT_ANALYSIS_MS_IN_DAY;

    return {
        startTimestamp,
        endTimestamp: startTimestamp + normalizedWindowWidthDays * PRODUCT_ANALYSIS_MS_IN_DAY,
    };
};

export const filterProductAnalysisReviewTimelineByTimeWindow = ({
    reviewTimeline,
    selectedTimeWindow,
}: {
    reviewTimeline: ProductAnalysisTimelineReview[];
    selectedTimeWindow: ProductAnalysisTimeWindow | null;
}): ProductAnalysisTimelineReview[] => {
    if (!selectedTimeWindow) {
        return [];
    }

    return reviewTimeline.filter(
        (review) =>
            review.timestamp >= selectedTimeWindow.startTimestamp && review.timestamp < selectedTimeWindow.endTimestamp,
    );
};

export const buildProductAnalysisReviewsTimelinePoints = ({
    selectedTimeWindow,
    reviewTimestamps,
    timelineAggregationWindowDays,
}: {
    selectedTimeWindow: ProductAnalysisTimeWindow | null;
    reviewTimestamps: number[];
    timelineAggregationWindowDays: number;
}): ProductAnalysisReviewsTimelinePoint[] => {
    if (!selectedTimeWindow) {
        return [];
    }

    const bucketDurationMs = Math.max(1, Math.round(timelineAggregationWindowDays)) * PRODUCT_ANALYSIS_MS_IN_DAY;
    const points: ProductAnalysisReviewsTimelinePoint[] = [];
    let timestampIndex = 0;

    for (
        let bucketStart = selectedTimeWindow.startTimestamp;
        bucketStart < selectedTimeWindow.endTimestamp;
        bucketStart += bucketDurationMs
    ) {
        const bucketEnd = Math.min(bucketStart + bucketDurationMs, selectedTimeWindow.endTimestamp);
        let reviewsCount = 0;

        while (timestampIndex < reviewTimestamps.length && reviewTimestamps[timestampIndex] < bucketStart) {
            timestampIndex += 1;
        }

        while (timestampIndex < reviewTimestamps.length && reviewTimestamps[timestampIndex] < bucketEnd) {
            reviewsCount += 1;
            timestampIndex += 1;
        }

        points.push({
            startTimestamp: bucketStart,
            endTimestamp: bucketEnd,
            reviewsCount,
        });
    }

    return points;
};

const rowIsInTimeWindow = ({
    row,
    selectedTimeWindow,
    reviewTimestampMap,
}: {
    row: ProductAnalysisProblemEntity;
    selectedTimeWindow: ProductAnalysisTimeWindow;
    reviewTimestampMap: Map<string, number>;
}): boolean => {
    const knownTimestamp = reviewTimestampMap.get(row.reviewId);
    const timestamp = knownTimestamp ?? parseProductAnalysisTimestamp(row.createdDate);

    return (
        timestamp !== null &&
        timestamp >= selectedTimeWindow.startTimestamp &&
        timestamp < selectedTimeWindow.endTimestamp
    );
};

export const buildFilteredProductAnalysisByTimeWindow = ({
    data,
    selectedTimeWindow,
    reviewTimestampMap,
    filteredReviewTimeline,
}: {
    data: ProductAnalysisEntity | null;
    selectedTimeWindow: ProductAnalysisTimeWindow | null;
    reviewTimestampMap: Map<string, number>;
    filteredReviewTimeline: ProductAnalysisTimelineReview[];
}): ProductAnalysisEntity | null => {
    if (!data || !selectedTimeWindow) {
        return data;
    }

    const negativeProblems = data.negativeProblems.filter((row) =>
        rowIsInTimeWindow({
            row,
            selectedTimeWindow,
            reviewTimestampMap,
        }),
    );
    const positiveProblems = data.positiveProblems.filter((row) =>
        rowIsInTimeWindow({
            row,
            selectedTimeWindow,
            reviewTimestampMap,
        }),
    );
    const neutralProblems = (data.neutralProblems || []).filter((row) =>
        rowIsInTimeWindow({
            row,
            selectedTimeWindow,
            reviewTimestampMap,
        }),
    );
    const analyzedReviewsCountInWindow = filteredReviewTimeline.reduce(
        (acc, review) => acc + (review.hasAnalysis ? 1 : 0),
        0,
    );

    const chartMetrics = new Map<
        string,
        {
            positiveSum: number;
            positiveCount: number;
            negativeSum: number;
            negativeCount: number;
            neutralSum: number;
            neutralCount: number;
        }
    >();
    const orderedProblemTypes: string[] = [];

    const ensureMetric = (value: string | null | undefined) => {
        const problemType = value?.trim();
        if (!problemType) {
            return null;
        }

        if (!chartMetrics.has(problemType)) {
            chartMetrics.set(problemType, {
                positiveSum: 0,
                positiveCount: 0,
                negativeSum: 0,
                negativeCount: 0,
                neutralSum: 0,
                neutralCount: 0,
            });
            orderedProblemTypes.push(problemType);
        }

        return chartMetrics.get(problemType) || null;
    };

    for (const point of data.chartPoints) {
        ensureMetric(point.problemType);
    }

    const accumulateMetric = (rows: ProductAnalysisProblemEntity[], tone: 'positive' | 'negative' | 'neutral') => {
        for (const row of rows) {
            if (row.intensity === null) {
                continue;
            }

            const metric = ensureMetric(row.problem);
            if (!metric) {
                continue;
            }

            if (tone === 'positive') {
                metric.positiveSum += row.intensity;
                metric.positiveCount += 1;
            } else if (tone === 'negative') {
                metric.negativeSum += row.intensity;
                metric.negativeCount += 1;
            } else {
                metric.neutralSum += row.intensity;
                metric.neutralCount += 1;
            }
        }
    };

    accumulateMetric(positiveProblems, 'positive');
    accumulateMetric(negativeProblems, 'negative');
    accumulateMetric(neutralProblems, 'neutral');

    const chartPoints = orderedProblemTypes.map((problemType) => {
        const metric = chartMetrics.get(problemType);

        if (!metric) {
            return {
                problemType,
                positiveAverageIntensity: 0,
                negativeAverageIntensity: 0,
                neutralAverageIntensity: 0,
                positiveCount: 0,
                negativeCount: 0,
                neutralCount: 0,
            };
        }

        return {
            problemType,
            positiveAverageIntensity: metric.positiveCount ? metric.positiveSum / metric.positiveCount : 0,
            negativeAverageIntensity: metric.negativeCount ? metric.negativeSum / metric.negativeCount : 0,
            neutralAverageIntensity: metric.neutralCount ? metric.neutralSum / metric.neutralCount : 0,
            positiveCount: metric.positiveCount,
            negativeCount: metric.negativeCount,
            neutralCount: metric.neutralCount,
        };
    });

    return {
        ...data,
        analyzedReviewsCount: analyzedReviewsCountInWindow,
        totalProblemsCount: negativeProblems.length + positiveProblems.length + neutralProblems.length,
        chartPoints,
        negativeProblems,
        positiveProblems,
        neutralProblems,
        reviewTimeline: filteredReviewTimeline.map((review) => ({
            reviewId: review.reviewId,
            createdDate: review.createdDate,
            hasAnalysis: review.hasAnalysis,
        })),
    };
};

export const buildProductAnalysisSelectedWindowEndTimestamp = ({
    selectedTimeWindow,
    timelineBounds,
}: {
    selectedTimeWindow: ProductAnalysisTimeWindow | null;
    timelineBounds: ProductAnalysisTimelineBounds | null;
}): number | null => {
    if (!selectedTimeWindow || !timelineBounds) {
        return null;
    }

    return Math.min(selectedTimeWindow.endTimestamp - 1, timelineBounds.maxTimestamp);
};
